// Data model for AssetList (design.md). Everything here lives in the browser only.

export type AssetClass = 'stock' | 'etf' | 'fund' | 'cash' | 'crypto';

/** FundSERV codes (FID5982, MFC5505…): three letters and 3–5 digits, no exchange listing. */
export const FUND_CODE_RE = /^[A-Z]{3}\d{3,5}$/;
export function looksLikeFundCode(code: string): boolean {
  return FUND_CODE_RE.test(code.trim().toUpperCase());
}
export type Currency = 'USD' | 'CAD' | 'CNY' | 'JPY' | 'EUR';
export const DISPLAY_CURRENCIES: Currency[] = ['USD', 'CAD', 'CNY', 'JPY', 'EUR'];
export const CRYPTO_SYMBOLS = ['BTC', 'ETH', 'USDC', 'USDT'] as const;

export interface Portfolio {
  id: string;
  name: string; // user-editable, e.g. "IB 美股账户"
  createdAt: number;
  sortOrder: number;
}

export type Market = 'US' | 'CA';
export const MARKETS: Market[] = ['US', 'CA'];

export interface Asset {
  id: string;
  portfolioId: string;
  assetClass: AssetClass;
  symbol: string; // ticker, crypto code, or ISO currency for cash
  name: string;
  exchange?: string; // raw exchange string from the data provider
  market?: Market; // listing market; undefined = US (records from before 2026-09-17)
  mic?: string; // ISO 10383 market identifier of the listing (XNAS, XTSE, NEOE…)
  listingType?: string; // provider instrument type: 'Common Stock', 'Depositary Receipt' (a CDR), 'ETF'…
  currency: string; // quote currency of the price (USD for US equities and crypto; the currency itself for cash)
  quantity: number;
  costPrice?: number;
  costCurrency?: string;
  purchaseDate?: string; // YYYY-MM-DD
  note?: string;
  manualPrice?: number; // used when no data key is configured; the NAV for mutual funds
  manualPriceAt?: number; // when manualPrice was last entered
  createdAt: number;
}

/**
 * A watch item is an asset added with quantity 0: it is priced, listed and polled like a holding, but it has no value,
 * so it never moves the totals, and it stays out of everything that reasons about the portfolio (reports, the expert
 * discussion, the prompt copy). Typing a quantity later turns it into a holding; nothing else is stored.
 */
export function isWatched(a: Asset): boolean {
  return a.quantity === 0;
}
/** The holdings proper — what the analysis features see. */
export function heldOnly(assets: Asset[]): Asset[] {
  return assets.filter((a) => !isWatched(a));
}

export type QuoteSource = 'finnhub' | 'massive' | 'twelvedata' | 'tmx' | 'coinbase' | 'frankfurter' | 'manual' | 'fixed';
/** `nav`: a mutual fund's published net asset value, dated by its publication day. */
export type Freshness = 'realtime' | 'delayed15' | 'close' | 'nav' | 'rate' | 'manual';

export interface Quote {
  key: string; // see quoteKey()
  price: number;
  currency: string;
  asOf: number;
  change?: number;
  changePct?: number;
  previousClose?: number;
  open?: number; // session open (equities) or the price 24 hours ago (crypto)
  high?: number; // session high, or the 24-hour high for crypto
  low?: number;
  source: QuoteSource;
  freshness: Freshness;
  stale: boolean;
}

export interface FxRates {
  base: 'USD';
  date: string; // ECB reference date
  rates: Record<string, number>; // 1 USD = rates[CCY] CCY
  fetchedAt: number;
}

export interface Snapshot {
  date: string; // YYYY-MM-DD (America/New_York)
  totalUsd: number;
  byPortfolio: Record<string, number>;
  byClass: Record<AssetClass, number>;
}

export type ReportTemplateId = 'premarket' | 'postmarket' | 'checkup' | 'stress';
export interface ReportSource { url: string; title?: string }
/** One generated report. Daily templates keep one per New York day; ad-hoc ones (checkup, stress) keep every run. */
export interface Report {
  id: string;
  templateId: ReportTemplateId;
  date: string; // New York calendar day
  generatedAt: number;
  provider: Provider;
  model: string;
  language: Locale;
  markdown: string;
  inputs: { totalUsd: number; positions: number; fxDate: string | null; scenario?: string; context: string };
  usage?: { inputTokens?: number; outputTokens?: number; searches?: number };
  queries?: string[];
  sources?: ReportSource[];
  costUsd?: number | null;
  searchSuggestionsHtml?: string; // Gemini grounding: Google's terms require showing this block with the result
}

// — LLM / expert discussion (same shape as SpinozaWeb so its prompt & orchestration code is reusable) —

export type Provider = 'openai' | 'gemini' | 'claude';

export interface ProviderSettings {
  apiKey: string;
  model: string;
  models?: string[];
  modelsFetchedAt?: number;
  modelsSource?: 'live' | 'fallback';
}

export interface DataKeyState {
  apiKey: string;
  verifiedAt?: number;
  status: 'unverified' | 'ok' | 'invalid';
  detail?: string;
}

export type Locale = 'zh-CN' | 'en' | 'fr-CA';

export interface Settings {
  llm: { activeProvider: Provider; providers: Partial<Record<Provider, ProviderSettings>> };
  data: { finnhub?: DataKeyState; massive?: DataKeyState; twelvedata?: DataKeyState };
  displayCurrency: Currency;
  locale?: Locale; // undefined = follow the browser
  setupDone: boolean;
}

export interface Thread {
  id: string;
  title: string;
  domainId: string;
  profileIds: string[];
  createdAt: number;
  updatedAt: number;
}

export type RoundType = 'answer' | 'debate';
export type MessageStatus = 'complete' | 'streaming' | 'error';

export interface Opinion {
  statement: string;
  confidence: number;
  analysis: string;
}

export interface Message {
  id: string;
  threadId: string;
  seq: number;
  round: number;
  roundType: RoundType;
  role: 'user' | 'profile';
  profileId?: string;
  text?: string;
  imageIds?: string[];
  opinions?: Opinion[];
  raw?: string;
  status: MessageStatus;
  error?: string;
  createdAt: number;
}

export const MIN_CONFIDENCE_DISPLAY = 0.1;
export const MAX_PROFILES_PER_THREAD = 8;

export function quoteKey(asset: Pick<Asset, 'assetClass' | 'symbol' | 'currency'> & { market?: Market }): string {
  if (asset.assetClass === 'cash') return `fx:${asset.currency.toUpperCase()}`;
  if (asset.assetClass === 'crypto') return `cx:${asset.symbol.toUpperCase()}`;
  // The same ticker can exist in both markets (a CDR of AAPL trades in Toronto), so Canadian keys carry the market.
  return asset.market === 'CA' ? `eq:CA:${asset.symbol.toUpperCase()}` : `eq:${asset.symbol.toUpperCase()}`;
}

/** Canadian Depositary Receipt: a CAD-priced, currency-hedged fraction of a US share, listed on Cboe Canada / TSX. */
export function isCdr(asset: Pick<Asset, 'market' | 'listingType'>): boolean {
  return asset.market === 'CA' && /depositary/i.test(asset.listingType ?? '');
}

/** One row of a provider's full symbol listing, kept for the local autocomplete index (market/symbolIndex.ts). */
export interface SymbolListing { symbol: string; name: string; type: string; mic: string; currency?: string }
export interface SymbolIndexRecord { key: string; fetchedAt: number; rows: SymbolListing[] }
